import type { inferProcedureInput, inferProcedureOutput } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { CalculatedRole } from '@viapeople/api/src/shared/shared.types'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../utils/trpc'

type Router = AppRouter['client']['tasks']
type InitialData = inferProcedureOutput<Router['initialize']>
export type Task = InitialData['tasks']['data'][number]
export type SearchTasksInput = inferProcedureInput<Router['search']>

interface State {
	cycle: InitialData['cycle']
	tasks: InitialData['tasks']
	taskCounts: InitialData['taskCounts']
	actions: InitialData['actions']
	searchTasksInput: SearchTasksInput
	searchTasksTimeout: NodeJS.Timeout | undefined
	error: string | null
	isInitializing: boolean
	isAdmin: boolean
	cycleId: string

	initialize: (cycleId: string, calculatedRole?: CalculatedRole) => Promise<void>
	setSearchTasksInput: (input?: SearchTasksInput) => void
	searchTasks: () => Promise<void>
	clearError: () => void
}

const api = trpcClient.client.tasks
export const useCycleTasksStore = create<State>()(
	devtools((set, get) => ({
		cycle: {},
		tasks: {},
		taskCounts: {},
		searchTasksInput: {},
		searchTasksTimeout: undefined,
		error: null,
		isInitializing: false,
		isAdmin: false,
		cycleId: '',

		initialize: async (cycleId, calculatedRole = CalculatedRole.PRIMARY_MANAGER) => {
			const { searchTasksTimeout } = get()
			if (searchTasksTimeout) {
				clearTimeout(searchTasksTimeout)
			}
			set({
				isInitializing: true,
				error: null,
				searchTasksInput: {
					cycleIds: [cycleId],
					includeCalculatedRoles: [calculatedRole],
				},
				searchTasksTimeout: undefined,
			})

			try {
				const initialData = await api.initialize.query({ cycleId, calculatedRole })

				const isAdmin = initialData.actions?.includes('searchEvaluations') ?? false

				set({
					cycle: initialData.cycle,
					tasks: initialData.tasks,
					taskCounts: initialData.taskCounts,
					actions: initialData.actions,
					isInitializing: false,
					isAdmin,
					cycleId,
				})
			} catch (error) {
				console.error('[cycleTasks.store] Failed to initialize cycle tasks:', error)
				set({
					error: error instanceof Error ? error.message : 'Error initializing cycle tasks',
					isInitializing: false,
				})
			}
		},

		setSearchTasksInput: (input) => {
			const { searchTasksTimeout } = get()

			if (searchTasksTimeout) {
				clearTimeout(searchTasksTimeout)
			}

			// Don't schedule search until we have a cycle id
			set(({ searchTasksInput, searchTasks, cycleId }) => ({
				searchTasksInput: {
					...searchTasksInput,
					...input,
				},
				searchTasksTimeout: cycleId ? setTimeout(searchTasks, 300) : undefined,
			}))
		},

		searchTasks: async () => {
			const { searchTasksInput, isAdmin, cycleId } = get()
			if (!cycleId) {
				console.warn('[cycleTasks.store] searchTasks called before cycleId was set, skipping.')
				return
			}

			try {
				const tasks = isAdmin
					? await api.searchCycleTasks.query({ ...searchTasksInput, cycleId })
					: await api.search.query(searchTasksInput)

				set({
					tasks,
				})
			} catch (error) {
				console.error('[cycleTasks.store] Failed to search tasks:', error)
				set({
					error: error instanceof Error ? error.message : 'Error searching tasks',
				})
			}
		},

		clearError: () => {
			set({
				error: null,
			})
		},
	})),
)
